// Dependencies
import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import { apiEndpoints, client } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import PostBody from "../../partials/postBody";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import CommentSection from "../../partials/comments/commentSection";
import ClientTheme from "../../core/clientTheme";
import PostImage from "../../partials/postImage";
import { logCustomEvent } from "../../core/analytics";
import Reactions from "../../partials/comments/reactions";
import { favoritesContentType } from "../../utils/helpers";

// UI components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Typography, Box, Link } from "@mui/material";
import TimeIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import MailIcon from "@mui/icons-material/EmailOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import PinIcon from "@mui/icons-material/PlaceOutlined";
import SinglesHeader from "../../partials/singlesHeader";

// Dummy data
const contentType = favoritesContentType("event");

export default function EventSingle(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [scrollHeight, setScrollHeight] = useState(null);
  // const [isCommentsLoading, setIsCommentsLoading] = useState(true);
  // const [comments, setComments] = useState([]);
  // const [pageSize, setPageSize] = useState(10);
  // const [currentPage, setCurrentPage] = useState(0);
  // const [total_pages, setTotal_Pages] = useState(0);
  // const [isReactionsLoading, setIsReactionsLoading] = useState(true);
  // const [reactions, setReactions] = useState(
  // {
  //   hasReacted: false,
  //   likes: 0,
  //   love: 0,
  //   suprised: 0,
  // },);

  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");

  const clientTheme = { ...ClientTheme() };

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "setHeight") {
        if (event.data.scrollHeight) {
          setScrollHeight(event.data.scrollHeight);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);


  const loadNode = () => {
    const nid = params.id;

    if (nid) {
      getContent(nid);
    } else {
      setErrorStatus(404);
      setErrorMessage("Not found");
      setIsError(true);
    }
  };



  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (nid) => {
    let path = `${apiEndpoints.events}/all/${nid}?_format=json&status=1&promote=1`;

    if (hash) {
      path = `/api/v2/shared?bundle=event&nid=${nid}&authkey=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.data.rows.length > 0) {
          setPost(response.data.rows[0]);
          logCustomEvent("read", response.data.rows[0], "Event");
          setIsError(false);
          setIsLoading(false);
        } else {
          setErrorStatus(404);
          setErrorMessage("No data");
          setIsError(true);
        }
      })
      .catch((_error) => {
        if (_error?.response?.status === 403) {
          setErrorStatus(403);
          setErrorMessage("You do not have access to view this content.");
          setIsError(true);
          setIsLoading(false);
        } else {
          setErrorStatus(500);
          setErrorMessage("Failed to get data");
          setIsError(true);
          setIsLoading(false);
        }
      });
  };

  const processTime = (time) => {
    if (time) {
      const milliseconds = parseInt(time.replace(/\n/g, "").trim()) * 1000;

      let date = new Date(milliseconds);
      let day = date.toLocaleString("en-GB", {
        dateStyle: "long",
        timeZone: "Europe/London",
      });
      let hour = date.toLocaleString("en-GB", {
        hour12: true,
        timeStyle: "short",
        timeZone: "Europe/London",
      });
      return { hour: hour, day: day };
    }
  };

  let start = processTime(post?.field_start_date);
  let end = processTime(post?.field_end_date);

  const renderEventDetails = () => {
    const iconStyle = {
      fontSize: 25,
      color: "clientPrimary.main",
    };

    const img = post.field_featured_image;
    const iconWidth = { xs: 2, sm: 1 };
    const contentWidth = { xs: 10, sm: 11 };
    return (
      <Box className="mb-5">
        <Row>
          <Col xs={4} className="d-none d-md-block">
            <PostImage
              imgSrc={img}
              nid={post.nid}
              className="ratio ratio-1x1 event-single-img-container"
            />
          </Col>
          <Col xs={12} md={8} className='d-flex flex-column ps-md-4 justify-content-between'>
            <Row className='d-flex flex-nowrap align-items-center justify-content-start mb-1 w-100'>
              <Col className='d-flex align-items-center' xs={iconWidth.xs} sm={iconWidth.sm}>
                <TimeIcon sx={iconStyle} />
              </Col>
              <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                <Typography
                  className="d-flex"
                  owner="client"
                  variant="body1"
                  color="clientText.main"
                >
                  {client === "equans" ? (
                    <>
                      {post.field_start_time === post.field_end_time
                        ? post.field_end_time
                        : `${post.field_start_time} - ${post.field_end_time}`}
                    </>
                  ) : (
                    <>
                      {start?.hour === end?.hour
                        ? end?.hour
                        : `${start?.hour} - ${end?.hour}`}
                    </>
                  )}
                </Typography>
              </Col>
            </Row>
            <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
              <Col
                className="d-flex align-items-center"
                xs={iconWidth.xs}
                sm={iconWidth.sm}
              >
                <CalendarIcon sx={iconStyle} />
              </Col>
              <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                <Typography
                  className="d-flex"
                  owner="client"
                  variant="body1"
                  color="clientText.main"
                >
                  {start?.day === end?.day
                    ? end?.day
                    : `${start?.day} - ${end?.day}`}
                </Typography>
              </Col>
            </Row>
            {post.field_contact_number && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <PhoneIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      href={`tel:${post.field_contact_number}`}
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                    >
                      {post.field_contact_number}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
            {post.field_email && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <MailIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`mailto: ${post.field_email}`}
                    >
                      {post.field_email}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
            {post.field_website && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <LinkIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`${post.field_website}`}
                      target="_blank"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {post.field_website}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
            {post.field_address && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <PinIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`http://maps.google.com/?q=${post.field_address}`}
                      target="_blank"
                    >
                      {post.field_address}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Box>
    );
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  } else {
    if (isError) {
      return <Error status={errorStatus} message={errorMessage} />;
    } else {
      let category = "Uncategorised";

      if (post.category_labels) {
        category = post.category_labels;
      }

      return (
        <main className="events single">
          <article className="article">
            <SinglesHeader
              route={"/events"}
              backBtnText={"Upcoming Events"}
              category={category}
              timestamp={post.created}
              title={post.title}
              hash={hash}
            />
            {renderEventDetails()}
            <PostBody body={post.body} />
            <div className="article__body">
            </div>
            {
              client === "companyapp" && post.view_node &&
              // client === "companyapp" && post.view_node && post.webform &&
              // above line commented out because post.webform is always null for non-admins. The view will not return any data for non-admins
              <div className="w-100">
                <iframe
                  className={`${iframeLoading && "invisible"} w-100`}
                  src={`${post.view_node}${post.view_node.includes('?') ? '&' : '?'}app=true`}
                  title={post.title}
                  frameBorder="0"
                  style={{
                    height: scrollHeight ? scrollHeight - 60 : "",
                  }}
                  onLoad={(iframe) => {
                    setIframeLoading(false);
                  }}
                />
              </div>
            }
            {user && user.current_user && (
              <>
                <Row className="reaction-container gx-0">
                  <Reactions
                    user={user}
                    nid={post.nid}
                    contentType={contentType}
                    field_allow_reactions={post.field_allow_reactions}
                    field_access_token={post.field_access_token}
                    title={post.title}
                    shareEnabled
                  />
                </Row>
                <CommentSection
                  nid={post.nid}
                  field_allow_reactions={post.field_allow_reactions}
                  field_allow_comments={post.field_allow_comments}
                />
              </>
            )}
          </article>
        </main>
      );
    }
  }
}
